import dayjs from 'dayjs';
import produce from 'immer';
import createCachedSelector from 're-reselect';
import { combineReducers } from 'redux';
import { createSelector, defaultMemoize } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { arrayShallowEqual } from '../../data-utils';
import { CallState, CallSuppressType, CallType, ClidObjectType, EntityType, ExtraCallData, getQueueOrSquadKey, Identifier, Media, QueueOrSquadId } from '../../model';
import { isDefined } from '../../type-utils';
import { Actions, StoreState } from '../types';
import { selectCallAssisants, selectMyDevices, selectMyself } from './config';
import { getParkedMedia, selectQueueHoldKey } from './localSettings';

type MediaEx = Media & {
  wrapupCodes: string[]
  queueInfo: undefined | {
    forcedWrapup: boolean
  }
  created: number
  storeId: string //used to create an id that is unquie to the store.  Electron embeded has a bug when a terminate and a broadcast arrive one after the other when do a queue hold
}

type MediaById = Record<string, MediaEx | undefined>

function manageExistingMediaState(next: Media, last?: MediaEx): MediaEx {

  let isRecording = false, voicemailPort = "", voicemailPortIsConnected = false,
    voicemailPortIsSuppresed: CallSuppressType = "allowed",
    hide: boolean = false, clid = next.clid, clidDescription = next.clidDescription, taskId = "", clidObjectKey = next.clidObjectKey,
    clidNumberType = next.clidNumberType, clidObjectType = next.clidObjectType, clidAccountCode = next.clidAccountCode, created = Date.now(),
    completed = false, minimised = 0, dtmf: boolean = false, window: boolean = false, notes: string = next.notes

  if (last) {
    isRecording = last.isRecording
    voicemailPort = last.voicemailPort
    voicemailPortIsConnected = last.voicemailPortIsConnected
    voicemailPortIsSuppresed = last.voicemailPortIsSuppresed
    hide = last.hide
    minimised = last.minimised
    taskId = last.taskId
    created = last.created
    completed = !last.completed ? false : last.completed
    dtmf = last.dtmf
    window = last.window

    //manage shown CLID when it has been hidden
    if (clid === '' && last.clid !== '') {
      clid = last.clid
      if (clidDescription === 'Unknown Caller') {
        clidDescription = ''
      }
    }

    if (last.clidObjectKey !== '' && next.clidObjectKey === '') {
      clidObjectKey = last.clidObjectKey
    }

    if (last.clidNumberType !== -1 && next.clidNumberType === -1) {
      clidNumberType = last.clidNumberType
    }

    if (last.clidObjectType !== -1 && next.clidObjectType === -1) {
      clidObjectType = last.clidObjectType
    }

    if (last.clidAccountCode !== '' && next.clidAccountCode === '') {
      clidAccountCode = last.clidAccountCode
    }

    if(last.notes !== '' && next.notes === '')
    {
      notes = last.notes
    }
  }

  return {
    id: next.id,
    deviceId: next.deviceId,
    status: next.status,
    clid,
    clidDescription,
    connectedParty: next.connectedParty,
    server: next.server,
    queueId: next.queueId,
    queueIdDescription: next.queueIdDescription,
    dnis: next.dnis,
    dnisDescription: next.dnisDescription,
    fullName: next.fullName,
    elapsedTime: next.elapsedTime,
    mediaType: next.mediaType,
    wrapupFolder: next.wrapupFolder,
    preferredExtn: next.preferredExtn,
    forwardFrom: next.forwardFrom,
    forwardReason: next.forwardReason,
    callType: next.callType,
    isQueueCall: next.isQueueCall,
    deviceType: next.deviceType,
    recordStatus: next.recordStatus,
    reason: next.reason,
    notes: notes,
    urgency: next.urgency,
    extensionId: next.extensionId,
    deviceDescription: next.deviceDescription,
    queuingTime: next.queuingTime,
    subject: next.subject,
    extensionsWaitingForWrapup: next.extensionsWaitingForWrapup,
    callTransactionId: next.callTransactionId,
    heldTime: next.heldTime,
    talkTime: next.talkTime,
    wrapupTime: next.wrapupTime,
    inVmRecordingMessage: next.inVmRecordingMessage,
    preferredExtnDescription: next.preferredExtnDescription,
    clidAccountCode,
    clidNumberType,
    clidObjectKey,
    clidObjectType,
    sourceApp: next.sourceApp,
    lastUpdateTime: next.lastUpdateTime,
    isRecording,
    voicemailPort,
    voicemailPortIsConnected,
    voicemailPortIsSuppresed,
    hide,
    minimised,
    taskId,
    mediaItemId: next.mediaItemId,
    extensionEmail: next.extensionEmail,
    devices: next.devices && next.devices.map((d) => ({
      number: d.number,
      baseType: d.baseType,
      description: d.description,
      visible: d.visible,
    })),
    wrapupCodes: last ? last.wrapupCodes : [],
    queueInfo: last ? last.queueInfo : undefined,
    created: created,
    storeId: last ? last.storeId : next.id + (Math.floor(Math.random() * 100000) + 1),
    completed: completed,
    dtmf: dtmf,
    window: window,
  }
}

function mediaById(state: MediaById = {}, action: Actions): MediaById {
  switch (action.type) {
    case getType(actions.fetchQueueMedia.response): {
      const newState = { ...state }
      for (const { agent, ...media } of action.payload.media) {
        newState[media.id] = manageExistingMediaState(media, state[media.id])
      }
      return newState
    }

    case getType(actions.shutdown): {
      //delete all which only happens on window unload
      return {}
    }

    case getType(actions.removeMedia): {
      const { id } = action.payload

      if (id) {
        const newState = { ...state }
        delete newState[id]
        return newState
      }

      //delete all which only happens on window unload
      return {}
    }

    case getType(actions.completeMedia): {
      const { mediaId } = action.payload
      const media = state[mediaId]
      if (!media) { return state }

      //if status none then delete
      if (media.status === CallState.None) {
        const newState = { ...state }
        delete newState[mediaId]
        return newState
      }

      //otherwise mark that complete has been clicked
      return editMedia(state, mediaId, { completed: true })
    }


    case getType(actions.updateMedia): {
      const media = action.payload.media
      return {
        ...state,
        [media.id]: manageExistingMediaState(media, state[media.id]),
      }
    }

    /*
    case getType(actions.startRecording): {
      const { callId, isRecording } = action.payload
      return editMedia(state, callId, {
        isRecording,
      })
    }
*/
    case getType(actions.updateMediaRecording): {
      const { callId, voicemailPort, isConnected } = action.payload

      const media = state[callId]
      if (!media) { return state }

      const isRecording = isConnected ? true : false;
      const vmPort = isConnected ? voicemailPort : '';

      return editMedia(state, callId, {
        voicemailPort: vmPort,
        voicemailPortIsConnected: isConnected,
        isRecording,
      })
    }

    case getType(actions.setMediaRecordingSuppress): {
      const { callId, suppress } = action.payload
      return editMedia(state, callId, { voicemailPortIsSuppresed: suppress })
    }

    case getType(actions.hideCall): {
      const { id, seconds } = action.payload
      return editMedia(state, id, { hide: seconds === undefined, minimised: seconds === undefined || seconds === 0 ? 0 : dayjs(Date.now()).add(seconds, 'second').valueOf() })
    }

    case getType(actions.setMediaNotes): {
      const { id, notes } = action.payload
      return editMedia(state, id, { notes })
    }

    case getType(actions.setMediaTaskId): {
      const { mediaId, taskId } = action.payload
      return editMedia(state, mediaId, { taskId })
    }

    case getType(actions.updateMediaClid):
    case getType(actions.fetchMediaClidResponse): {
      const { mediaId, clid } = action.payload
      const clidDescription = ''
      return editMedia(state, mediaId, { clid, clidDescription })
    }

    case getType(actions.updateMediaClidObject): {
      //reset task id too
      const { mediaId, clidObjectKey, clidObjectType, clidDescription } = action.payload
      return editMedia(state, mediaId, { clidObjectKey, clidObjectType, clidDescription, taskId: '' })
    }

    case getType(actions.updateMediaSubject): {
      const { mediaId, subject } = action.payload
      return editMedia(state, mediaId, { subject })
    }

    case getType(actions.updateMediaQueueInfo): {
      const { mediaId, forcedWrapup } = action.payload
      return editMedia(state, mediaId, { queueInfo: { forcedWrapup } })
    }

    case getType(actions.setWrapupCodes): {
      const { mediaId, codes } = action.payload
      return editMedia(state, mediaId, { wrapupCodes: codes })
    }

    case getType(actions.transferMedia): {
      const { id, to } = action.payload
      return editMedia(state, id, { server: to.server, queueId: to.queue, extensionId: to.agent || undefined })
    }

    case getType(actions.updateMediaStatus): {
      const { mediaId, status } = action.payload

      if (status === CallState.None) {
        //hack to maintain times
        const media = state[mediaId]
        if (!media) {
          return state
        }

        const now = Date.now()
        const time = Math.floor((now - media.elapsedTime) / 1000)

        return {
          ...state,
          [mediaId]: {
            ...media,
            status,
            queuingTime: media.status === CallState.Queueing || media.status === CallState.AnnouncementAnswered || media.status === CallState.AnnouncementPending ? time : media.queuingTime,
            wrapupTime: media.status === CallState.WaitingForWrapup ? time : media.wrapupTime,
            heldTime: media.status === CallState.QueueHold || media.status === CallState.Hold ? time : media.heldTime,
            talkTime: media.status === CallState.ExtensionAnswered || media.status === CallState.ExtensionTransfer ? time : media.talkTime,
          },
        }
      }

      return editMedia(state, mediaId, { status })
    }

    case getType(actions.updateMediaShowDtmf): {
      const { mediaId, dtmf } = action.payload
      return editMedia(state, mediaId, { dtmf })
    }

    case getType(actions.setWindowOnMedia): {
      const { mediaId } = action.payload
      return editMedia(state, mediaId, { window: true })
    }

    case getType(actions.deleteContact): {
      return produce(state, state => {
        const { contact } = action.payload
        for (const key in state) {
          if (Object.prototype.hasOwnProperty.call(state, key)) {
            const element = state[key];
            if (element) {
              if (element.clidObjectKey === contact.id) {
                element.clidObjectKey = ''
                element.clidObjectType = ClidObjectType.none
                element.clidDescription = ''
              }
            }
          }
        }
      })
    }

    case getType(actions.forceReload):
      {
        return {}
      }

    default:
      return state
  }
}

function editMedia(state: MediaById, id: string, newData: Partial<MediaEx>) {
  const media = state[id]
  if (!media) { return state }
  return {
    ...state,
    [id]: {
      ...media,
      ...newData,
    },
  }
}

type Loaded = Record<string, boolean | undefined>
function loaded(state: Loaded = {}, action: Actions): Loaded {
  switch (action.type) {
    case getType(actions.fetchQueueMedia.request): {
      return {
        ...state,
        [getQueueOrSquadKey(action.payload)]: state[getQueueOrSquadKey(action.payload)] || false,
      }
    }

    case getType(actions.fetchQueueMedia.response): {
      return {
        ...state,
        [getQueueOrSquadKey(action.payload.queue)]: true,
      }
    }

    case getType(actions.forceReload):
      {
        return {}
      }
  }
  return state
}

const emptyStringArray: String[] = []
function autoAnwser(state = emptyStringArray, action: Actions): String[] {
  switch (action.type) {
    case getType(actions.ccSetForAutoAnswer): {
      return [...state, action.payload.callId]
    }

    case getType(actions.ccRemoveForAutoAnswer): {
      return state.filter(x => x !== action.payload.callId)
    }

  }
  return state
}

function lastActive(state: string | null = null, action: Actions): string | null {
  switch (action.type) {
    case getType(actions.openAgentView):
      return action.payload.mediaId || null
  }

  return state
}

export interface WrapupNotification {
  media: string[]
  minimise: number
  updated: number
}

const empty = {
  media: [],
  minimise: 0,
  updated: Date.now(),
} as WrapupNotification

function wrapupNotification(state: WrapupNotification = empty, action: Actions): WrapupNotification {
  switch (action.type) {
    case getType(actions.updateMedia): {
      const { media, me, parked } = action.payload
      //need to more me, parked out of here but this works for now
      if (doesMediaBelongToExtension(media, me) || parked.includes(media.id)) {
        if (media.status === CallState.WaitingForWrapup || (parked.includes(media.id) && media.extensionsWaitingForWrapup.includes(me.id))) {
          if (!state.media.find(x => x === media.id)) {
            const calls = state.media
            const update = calls.length === 0
            return { ...state, media: [...calls, media.id], updated: update ? Date.now() : state.updated }
          }
          return state
        }
      }

      //if we don't add it then we remove it
      if (state.media.find(x => x === media.id)) {
        const calls = state.media.filter(x => x !== media.id)
        return { ...state, media: [...calls] }
      }

      return state
    }

    case getType(actions.shutdown): {
      return empty
    }

    case getType(actions.removeMedia): {
      const { id } = action.payload
      const calls = state.media.filter(x => x !== id)
      return { ...state, media: [...calls] }
    }

    case getType(actions.hideWrapupNotification):
      const { seconds } = action.payload
      return {
        ...state, minimise: seconds > 0 ? dayjs(Date.now()).add(seconds, 'second').valueOf() : 0,
      }
  }

  return state
}

type ExtraCallDataById = Record<string, ExtraCallData | undefined>
function mediaExtraData(state: ExtraCallDataById = {}, action: Actions): ExtraCallDataById {
  switch (action.type) {
    case getType(actions.removeMedia): {
      const { id } = action.payload

      if (id) {
        const newState = { ...state }
        delete newState[id]
        return newState
      }

      return {}
    }

    case getType(actions.updateMediaExtraData):
      {
        const { mediaId, data } = action.payload
        return {
          ...state,
          [mediaId]: data,
        }
      }

    case getType(actions.updateMediaClidObject): {
      const { mediaId } = action.payload
      const newState = { ...state }
      delete newState[mediaId]
      return newState
    }

    case getType(actions.deleteContact): {
      return produce(state, state => {
        const { contact } = action.payload
        if (state) {
          for (const key in state) {
            if (Object.prototype.hasOwnProperty.call(state, key)) {
              const element = state[key];
              if (element && element.id === contact.id && element.type === EntityType.Contact) {
                delete state[key];
              }
            }
          }
        }
      })
    }

    case getType(actions.updateContactNumber):
    case getType(actions.deleteContactNumber):
    case getType(actions.updateContactDetails):
      {
        return produce(state, state => {
          const { contactId } = action.payload
          if (state) {
            for (const key in state) {
              if (Object.prototype.hasOwnProperty.call(state, key)) {
                const element = state[key];
                if (element && element.id === contactId.toString() && element.type === EntityType.Contact) {
                  delete state[key];
                }
              }
            }
          }
        })
      }
  }
  return state
}

export default combineReducers({
  mediaById,
  loaded,
  autoAnwser,
  lastActive,
  wrapupNotification,
  mediaExtraData,
})

const emptyArray: never[] = []

function getRoot(state: StoreState) { return state.media }
export function getMediaById(state: StoreState) { return state.media.mediaById }
export function getForAutoAnswer(state: StoreState) { return getRoot(state).autoAnwser }
function getLastActive(state: StoreState) { return getRoot(state).lastActive }
export function getWrapupNotification(state: StoreState) { return getRoot(state).wrapupNotification }
function getMediaExtraData(state: StoreState) { return getRoot(state).mediaExtraData }

const getLoaded = (state: StoreState, id: QueueOrSquadId) => getRoot(state).loaded[getQueueOrSquadKey(id)]
export function selectMediaIsLoading(state: StoreState, id: QueueOrSquadId) { return getLoaded(state, id) === false }
export function shouldFetchQueueMedia(state: StoreState, id: QueueOrSquadId) { return getLoaded(state, id) === undefined }

export const selectAllMedia = createSelector(
  getMediaById,
  media => Object.values(media).filter(isDefined),
)

export function selecMediaExtraData(state: StoreState, id: string) {
  return getMediaExtraData(state)[id]
}

export function selectMedia(state: StoreState, id: string) {
  return getMediaById(state)[id]
}

function doesMediaBelongToExtension(media: Media, agent: Identifier) {
  //return media.server === agent.server && ( media.extensionId === agent.id || media.queueId === agent.id || media.extensionsWaitingForWrapup.includes(agent.id))
  return media.server === agent.server && ((media.status !== CallState.WaitingForWrapup && media.extensionId === agent.id) || media.queueId === agent.id || media.extensionsWaitingForWrapup.includes(agent.id))
}

const selectMyMediaRaw = defaultMemoize((media: MediaEx[]) => media, arrayShallowEqual as any)

export const selectMyMedia = createSelector(
  selectMyself,
  (state: StoreState) => {
    const myself = selectMyself(state)
    const parked = getParkedMedia(state)
    return myself ? selectMyMediaRaw(selectAllMedia(state).filter(m => doesMediaBelongToExtension(m, myself) || parked.includes(m.id))) : []
  },
  selectMyDevices,
  (myId, media, myDevices) => {
    if (!myId || !myDevices) { return [] }

    return media.map(m => {
      if (m.isQueueCall && m.status !== CallState.WaitingForWrapup && !(m.extensionId === myId.id || (m.devices && myDevices.devices.some(d => d.number === m.deviceId))) && m.extensionsWaitingForWrapup.some(x => x === myId.id)) {
        return { ...m, status: CallState.WaitingForWrapup }
      } else {
        return m
      }
    })
  },
)

const selectAssitantMediaRaw = defaultMemoize((media: MediaEx[]) => media, arrayShallowEqual as any)

export const selectCallAssisantMedia = createSelector(
  (state: StoreState) => {
    const assisants = selectCallAssisants(state)
    if (assisants.length === 0) {
      return selectAssitantMediaRaw([])
    }

    const d = selectAllMedia(state).filter(m => {
      let yes = false
      for (let index = 0; index < assisants.length; index++) {
        const assisant = assisants[index];
        if ((m.mediaType === 'CALL' || m.mediaType === 'VM') && m.server === assisant.server && m.extensionId === assisant.id || m.queueId === assisant.id) {
          if ((m.status === CallState.ExtensionPending || m.status === CallState.CallInVoicemail) && (m.callType === CallType.Queue || m.callType === CallType.Incoming)) {
            yes = true;
            index = assisants.length
          }
        }
      }
      return yes
    },
    )
    return selectAssitantMediaRaw(d)
  },
  (media) => {
    if (!media) { return [] }

    return media
  },
)

export const selectMyCalls = createSelector(
  selectMyMedia,
  media => media.filter(m => m.mediaType === 'CALL' || m.mediaType === 'VM'),
)

export const selectMyActiveCalls = createSelector(
  selectMyCalls,
  selectMyself,
  selectMyDevices,
  (calls, me, myDevices) => {

    if (!me || !myDevices) return []

    return calls.filter(call => {
      if (call.status !== CallState.WaitingForWrapup) {
        //if call is out our extension
        // added call.queueId as this is your extension when at voicemail
        return (call.queueId === me.id || call.deviceId === me.id || call.extensionId === me.id || (call.devices && myDevices.devices.find(d => d.number === call.deviceId)))
      }
      return false
    })
  },
)

export const selectMyActiveCall = createSelector(
  selectMyActiveCalls,
  (calls) => {
    let best: Media | null = null;

    for (const call of calls) {
      if (best === null) {
        best = call
        continue
      }

      if (call.status === CallState.Hold && best.status === CallState.Hold) {
        if (call.lastUpdateTime > best.lastUpdateTime) {
          best = call
        }
      } else if (call.status !== CallState.Hold) {
        best = call
      }
    }
    return best
  },
)

export const selectHeldCall = createSelector(
  selectMyCalls,
  calls => {
    let best: Media | null = null

    for (const call of calls) {
      if (call.status !== CallState.Hold) { continue }

      if (best === null || call.lastUpdateTime > best.lastUpdateTime) {
        best = call
      }
    }

    return best
  },
)

//last media put into park by client
export const selectQueueHeldCall = createSelector(
  (s: StoreState) => {
    const key = selectQueueHoldKey(s)
    if (key === "") return null
    const media = selectMedia(s, key)
    if (!media) return null

    return media
  },
  call => {
    return call
  },
)

export const selectMediaByTaskId = createCachedSelector(
  selectMyMedia,
  (s: StoreState, taskId: string) => taskId,
  (media, taskId) => {
    for (const m of media) {
      if (m.taskId.replace('{', '').replace('}', '').toUpperCase() === taskId.replace('{', '').replace('}', '').toUpperCase()) {
        return m
      }
    }
    return null
  },
)(
  (_, taskId) => taskId,
)

export const selectMediaByContactId = createCachedSelector(
  selectMyMedia,
  (s: StoreState, contactId: string) => contactId,
  (media, contactId) => {
    return media.filter(c => c.clidObjectKey === contactId && (c.clidObjectType === ClidObjectType.companyContact || c.clidObjectType === ClidObjectType.customerContact || c.clidObjectType === ClidObjectType.personalContact))
  },
)(
  (_, contactId) => contactId,
)


export const selectMediaNotes = createCachedSelector(
  selectMedia,
  (media) => {
    if (!media) return ''
    return media.notes
  },
)(
  (_, id) => id,
)

export function selectMediaWrapupCodes(state: StoreState, mediaId: string) {
  const media = selectMedia(state, mediaId)
  return media ? media.wrapupCodes : emptyArray
}

export function shouldFetchMediaQueueInfo(state: StoreState, mediaId: string) {
  const media = selectMedia(state, mediaId)
  return media ? media.queueInfo === undefined : false
}

export function selectMyCurrentMedia(state: StoreState) {
  const id = getLastActive(state)
  if (!id) { return null }
  const media = selectMyMedia(state)
  return media.find(m => m.id === id) || null
}

export const selectMyActiveMedia = createSelector(
  selectMyCurrentMedia,
  selectMyMedia,
  (selected, calls) => {
    //if I have picked one in agent view select it
    if (selected) return selected

    return null
    //otherwsie find the best on in the list
    /*
    let best: Media | null = null;

    for (const call of calls) {
      if (best === null) {
        best = call
        continue
      }

      if ((call.mediaType === 'CALL' || call.mediaType === 'VM') && (best.mediaType !== 'CALL' && best.mediaType !== 'VM')) {
        best = call
      } else if ((best.mediaType === 'CALL' || best.mediaType === 'VM') && (call.mediaType !== 'CALL' && call.mediaType !== 'VM')) {
        // best stays best
      } else if (call.status === CallState.Hold && best.status === CallState.Hold) {
        if (call.lastUpdateTime > best.lastUpdateTime) {
          best = call
        }
      } else if (call.status !== CallState.Hold) {
        best = call
      }
    }
    return best
    */
  },
)
